import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import { IoSearchOutline, IoCloseCircle, IoChevronDown } from 'react-icons/io5';
import { BsTicketPerforated, BsFolder2Open, BsPerson, BsArrowRightShort, BsTrash } from 'react-icons/bs';
import { baseURL } from '../../helpers/baseURL';
import { ucFirst } from '../../helpers/generic';
import Highlight from './Highlight';
import FacetMenu from './FacetMenu';
import { statusBadgeStyle, statusColor } from './statusBadge';
import './globalSearch.css';

const TABS = [
    { key: 'all', label: 'All' },
    { key: 'members', label: 'Members', icon: <BsPerson /> },
    { key: 'tickets', label: 'Tickets', icon: <BsTicketPerforated /> },
    { key: 'departments', label: 'Departments', icon: <BsFolder2Open /> },
];

/** What an empty tab calls the thing it could not find. */
const TAB_NOUNS = {
    all: 'results',
    members: 'members',
    tickets: 'tickets',
    departments: 'departments',
};

const TRANSFER_OPTIONS = [
    { value: 'out', label: 'Transferred out' },
    { value: 'in', label: 'Received by transfer' },
];

const EMPTY_FILTERS = { members: [], departments: [], statuses: [], transferred: '' };

const EMPTY_RESULTS = {
    query: '',
    page: 1,
    counts: { all: 0, tickets: 0, members: 0, departments: 0 },
    shown: { tickets: 0 },
    has_more: false,
    tickets: [],
    members: [],
    departments: [],
};

const MIN_TERM_LENGTH = 2;
const DEBOUNCE_MS = 250;
/** Distance from the bottom of the list that triggers loading the next page. */
const LOAD_MORE_THRESHOLD_PX = 160;

const authHeaders = () => ({
    headers: { Authorization: `Bearer ${Cookies.get('access_token')}` },
});

/** Turn a status name into the class suffix used by the badge styles. */
const statusSlug = (name) => String(name ?? '').toLowerCase().trim().replace(/\s+/g, '-');

/**
 * Whether a tab's filter menu is open. Read from the DOM rather than tracked in
 * a counter, which could drift out of sync and silently kill result navigation.
 * The menu is portaled to <body>, hence the global lookup.
 */
const isFacetOpen = () => !!document.querySelector('.ck-gs-facet');

/** Tabs that carry a filter menu, and what that menu filters. */
const FACET_TABS = ['members', 'tickets', 'departments'];

/** How many scope filters are set. A filter-only search is a valid search. */
const countActiveFilters = (filters) =>
    (filters.members?.length ?? 0)
    + (filters.departments?.length ?? 0)
    + (filters.statuses?.length ?? 0)
    + (filters.transferred ? 1 : 0);

/** Query string for one search request. */
const buildParams = (term, filters, page) => {
    const params = new URLSearchParams();
    if (term.trim()) {
        params.set('q', term.trim());
    }
    ['members', 'departments', 'statuses'].forEach((key) => {
        if (filters[key]?.length) {
            params.set(key, filters[key].join(','));
        }
    });
    if (filters.transferred) {
        params.set('transferred', filters.transferred);
    }
    if (page > 1) {
        params.set('page', String(page));
    }
    return params.toString();
};

/** Identifies one search, so a late page-2 response can be matched to it. */
const criteriaKey = (term, filters) => JSON.stringify([term.trim(), filters]);

const GlobalSearch = ({ show, onClose }) => {
    const history = useHistory();
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const cancelRef = useRef(null);

    const [query, setQuery] = useState('');
    // Typing is debounced into this; filter changes bypass the wait entirely.
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [results, setResults] = useState(EMPTY_RESULTS);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [failed, setFailed] = useState(false);
    const [activeTab, setActiveTab] = useState('all');
    const [activeIndex, setActiveIndex] = useState(0);
    const [scopes, setScopes] = useState({ members: [], departments: [], statuses: [] });
    // Which tab's filter menu is open, and where to anchor it.
    const [facet, setFacet] = useState(null);

    // Set while navigating by keyboard so the pointer resting over a row does not
    // yank the highlight back; cleared by the next real mouse movement.
    const suppressHoverRef = useRef(false);
    // Guards against a scroll burst firing several page loads at once.
    const loadingMoreRef = useRef(false);
    // Identifies the search the current results belong to.
    const criteriaRef = useRef('');

    // Always the text on screen, for callers that must not wait for the debounce.
    const queryRef = useRef(query);
    queryRef.current = query;

    const scopesLoaded = scopes.members.length || scopes.departments.length || scopes.statuses.length;

    // Scope options are workspace-stable; fetch them once the palette first opens.
    useEffect(() => {
        if (!show || scopesLoaded) {
            return;
        }
        let cancelled = false;
        axios.get(`${baseURL}search-scopes`, authHeaders())
            .then(({ data }) => {
                if (cancelled) return;
                setScopes({
                    members: data?.members ?? [],
                    departments: data?.departments ?? [],
                    statuses: data?.statuses ?? [],
                });
            })
            .catch(() => { /* filters simply stay empty */ });
        return () => { cancelled = true; };
    }, [show, scopesLoaded]);

    const focusInput = useCallback(() => {
        // Deferred so the click that triggered it finishes first.
        setTimeout(() => inputRef.current?.focus(), 0);
    }, []);

    const closeFacet = useCallback(() => setFacet(null), []);

    /** Open (or re-close) a tab's filter menu, anchored under the tab. */
    const toggleFacet = useCallback((tabKey, element) => {
        setFacet((prev) => {
            if (prev?.tab === tabKey) {
                return null;
            }
            const rect = element.getBoundingClientRect();
            return { tab: tabKey, top: rect.bottom + 6, left: rect.left };
        });
    }, []);

    // A filter menu is anchored to a fixed position, so it has to go when the
    // page moves underneath it.
    useEffect(() => {
        if (!facet) return undefined;
        const onDismiss = (event) => {
            // Scrolling the result list does not move the tabs, so leave the menu
            // alone for that; scrolling the palette itself does move them.
            if (event?.type === 'scroll' && event.target?.closest?.('.ck-gs-body')) {
                return;
            }
            setFacet(null);
        };
        window.addEventListener('resize', onDismiss);
        window.addEventListener('scroll', onDismiss, true);
        return () => {
            window.removeEventListener('resize', onDismiss);
            window.removeEventListener('scroll', onDismiss, true);
        };
    }, [facet]);

    useEffect(() => {
        if (!show) {
            return undefined;
        }
        // Let the overlay paint before stealing focus, otherwise mobile
        // Safari scrolls the page instead of opening the keyboard.
        const id = setTimeout(() => inputRef.current?.focus(), 20);
        return () => clearTimeout(id);
    }, [show]);

    /**
     * Freeze the page behind the palette without letting anything jump sideways.
     *
     * Hiding the body's overflow removes the page scrollbar, and the layout then
     * reflows into the freed width — the whole page slides right by the scrollbar
     * width. Measuring that width first and holding it open as padding keeps
     * everything exactly where it was.
     *
     * This has to be a layout effect. A passive effect runs after the browser has
     * painted, so the palette would be drawn once against the still-scrollbarred
     * viewport and again after the scrollbar went away — and because the overlay
     * is fixed and centres its contents, that second paint lands half a scrollbar
     * to the right. Locking before the first paint means there is only one.
     */
    useLayoutEffect(() => {
        if (!show) {
            return undefined;
        }

        // Must be measured before the scrollbar is taken away.
        const gutter = window.innerWidth - document.documentElement.clientWidth;
        if (gutter > 0) {
            document.documentElement.style.setProperty('--ck-gs-gutter', `${gutter}px`);
        }
        document.body.classList.add('ck-gs-lock');

        return () => {
            document.body.classList.remove('ck-gs-lock');
            document.documentElement.style.removeProperty('--ck-gs-gutter');
        };
    }, [show]);

    const runSearch = useCallback((term, activeFilters) => {
        cancelRef.current?.cancel('superseded');

        // Either enough text to search on, or at least one filter to search by.
        const searchable = term.replace(/^#/, '').trim().length >= MIN_TERM_LENGTH
            || countActiveFilters(activeFilters) > 0;

        if (!searchable) {
            criteriaRef.current = '';
            setResults(EMPTY_RESULTS);
            setLoading(false);
            setFailed(false);
            return;
        }

        const source = axios.CancelToken.source();
        cancelRef.current = source;
        const signature = criteriaKey(term, activeFilters);
        criteriaRef.current = signature;
        setLoading(true);
        setFailed(false);

        const url = `${baseURL}search?${buildParams(term, activeFilters, 1)}`;
        axios.get(url, { ...authHeaders(), cancelToken: source.token })
            .then(({ data }) => {
                // Cancellation is the first line of defence, but a response that
                // was already on the wire (or served from cache) can still land
                // after a newer search started. Applying it would show results
                // for a query the user has moved on from, so drop it.
                if (criteriaRef.current !== signature) {
                    return;
                }
                setResults({ ...EMPTY_RESULTS, ...data });
                setActiveIndex(0);
                setLoading(false);
            })
            .catch((error) => {
                if (axios.isCancel(error) || criteriaRef.current !== signature) return;
                setResults(EMPTY_RESULTS);
                setFailed(true);
                setLoading(false);
            });
    }, []);

    /**
     * Append the next page of tickets. Guarded so a burst of scroll events cannot
     * fire it twice, and the response is dropped if the search criteria moved on
     * while it was in flight.
     */
    const loadMore = useCallback(() => {
        if (loadingMoreRef.current || !results.has_more || loading) {
            return;
        }

        const signature = criteriaRef.current;
        if (!signature) {
            return;
        }

        loadingMoreRef.current = true;
        setLoadingMore(true);

        const nextPage = (results.page ?? 1) + 1;
        const url = `${baseURL}search?${buildParams(queryRef.current, filters, nextPage)}`;

        axios.get(url, authHeaders())
            .then(({ data }) => {
                if (criteriaRef.current !== signature) {
                    return;
                }
                setResults((prev) => {
                    // De-dupe defensively: rows must never repeat across pages.
                    const seen = new Set(prev.tickets.map((ticket) => ticket.id));
                    const added = (data.tickets ?? []).filter((ticket) => !seen.has(ticket.id));
                    // Later pages carry tickets only: the API stops returning
                    // member and department rows past page 1, so the zeroes it
                    // reports for them must not overwrite what page 1 found.
                    const memberCount = prev.counts.members ?? 0;
                    const departmentCount = prev.counts.departments ?? 0;
                    const ticketCount = data.counts?.tickets ?? prev.counts.tickets ?? 0;
                    return {
                        ...prev,
                        page: data.page ?? nextPage,
                        has_more: !!data.has_more,
                        shown: data.shown ?? prev.shown,
                        counts: {
                            all: ticketCount + memberCount + departmentCount,
                            tickets: ticketCount,
                            members: memberCount,
                            departments: departmentCount,
                        },
                        tickets: [...prev.tickets, ...added],
                    };
                });
            })
            .catch(() => { /* keep what is already on screen */ })
            .finally(() => {
                loadingMoreRef.current = false;
                setLoadingMore(false);
            });
    }, [results.has_more, results.page, loading, filters]);

    const handleBodyScroll = (event) => {
        const el = event.currentTarget;
        const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (remaining <= LOAD_MORE_THRESHOLD_PX) {
            loadMore();
        }
    };

    // Typing settles before it costs a request.
    useEffect(() => {
        if (!show) return undefined;
        const id = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
        return () => clearTimeout(id);
    }, [query, show]);

    // Picking a filter is a deliberate act, so it searches at once. The live
    // query text is read from the ref so a filter chosen mid-typing still uses
    // what is on screen rather than the last debounced value.
    useEffect(() => {
        if (!show) return;
        runSearch(queryRef.current, filters);
    }, [debouncedQuery, filters, show, runSearch]);

    useEffect(() => () => cancelRef.current?.cancel('unmounted'), []);

    const close = useCallback(() => {
        cancelRef.current?.cancel('closed');
        onClose?.();
    }, [onClose]);

    // Clears the typed text only; any active filters keep driving the results.
    const reset = () => {
        setQuery('');
        setDebouncedQuery('');
        setActiveIndex(0);
        focusInput();
    };

    const goTo = useCallback((url) => {
        close();
        history.push(url);
    }, [close, history]);

    /** Add or remove one id from a multi-value filter. */
    const toggleFilterValue = useCallback((key, id) => {
        setFilters((prev) => {
            const current = prev[key] ?? [];
            const numeric = Number(id);
            return {
                ...prev,
                [key]: current.includes(numeric)
                    ? current.filter((value) => value !== numeric)
                    : [...current, numeric],
            };
        });
    }, []);

    const setFilterValues = (key, selection) => {
        setFilters((prev) => ({ ...prev, [key]: (selection ?? []).map((option) => option.value) }));
    };

    const activeFilterCount = countActiveFilters(filters);

    // Who and what actually appears in the results on screen. Those options sort
    // to the top of their menu and are marked, so the menu reads as "people in
    // these results" first while still offering everyone in the workspace —
    // filtering is applied by the server, not by hiding fetched rows, so a
    // member with nothing on this page still returns their tickets.
    const inResults = useMemo(() => {
        const members = new Set();
        const departments = new Set();
        const statuses = new Set();
        results.tickets.forEach((ticket) => {
            (ticket.assignees ?? []).forEach((assignee) => members.add(assignee.id));
            if (ticket.department?.id) departments.add(ticket.department.id);
            if (ticket.status?.id) statuses.add(ticket.status.id);
        });
        results.members.forEach((member) => members.add(member.id));
        results.departments.forEach((dept) => departments.add(dept.id));
        return { members, departments, statuses };
    }, [results]);

    /**
     * One flat list of everything currently on screen, in visual order, so the
     * arrow keys move through sections the way the eye does.
     */
    const navRows = useMemo(() => {
        const rows = [];
        const wants = (tab) => activeTab === 'all' || activeTab === tab;

        if (wants('members')) {
            results.members.forEach((m) => rows.push({ kind: 'member', id: m.id, url: m.url }));
        }
        if (wants('tickets')) {
            results.tickets.forEach((t) => rows.push({ kind: 'ticket', id: t.id, url: t.url }));
        }
        if (wants('departments')) {
            results.departments.forEach((d) => rows.push({ kind: 'department', id: d.id, url: d.url }));
        }
        return rows;
    }, [results, activeTab]);

    // Mirrors for the document-level key handler, so it can stay registered once
    // instead of being torn down on every highlight change.
    const navRowsRef = useRef(navRows);
    navRowsRef.current = navRows;
    const activeIndexRef = useRef(activeIndex);
    activeIndexRef.current = activeIndex;
    const activeTabRef = useRef(activeTab);
    activeTabRef.current = activeTab;
    const countsRef = useRef(results.counts);
    countsRef.current = results.counts;
    // Assigned below, once the filters and scopes it reads are in scope.
    const ticketsUrlRef = useRef(() => '/tickets');

    useEffect(() => {
        setActiveIndex(0);
    }, [activeTab]);

    // Keep the highlighted row inside the scroll viewport.
    useEffect(() => {
        const node = listRef.current?.querySelector('.ck-gs-active');
        node?.scrollIntoView({ block: 'nearest' });
    }, [activeIndex, navRows.length]);

    // Every tab is selectable, empty ones included, so Tab walks all of them.
    const cycleTab = useCallback((direction) => {
        const current = Math.max(0, TABS.findIndex((tab) => tab.key === activeTabRef.current));
        const next = (current + direction + TABS.length) % TABS.length;
        setActiveTab(TABS[next].key);
    }, []);

    /**
     * Keys are handled on the document, not the input, so navigation keeps working
     * after the focus has moved to a tab button or a scope filter.
     *
     * Capture phase matters: React dispatches its own handlers from a document
     * listener, so a plain listener here would run after a click or key had
     * already changed the DOM. Running first is what lets Escape close an open
     * filter menu rather than the whole palette.
     */
    useEffect(() => {
        if (!show) return undefined;

        const onKeyDown = (event) => {
            const menuOpen = isFacetOpen();

            if (event.key === 'Escape') {
                event.preventDefault();
                // An open filter menu swallows the first Escape to close itself.
                if (menuOpen) {
                    setFacet(null);
                    return;
                }
                close();
                return;
            }

            // While a filter has focus its own arrow/enter behaviour wins.
            if (menuOpen || event.target?.closest?.('.ck-gs-facet')) {
                return;
            }

            const rows = navRowsRef.current;

            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    suppressHoverRef.current = true;
                    if (rows.length) setActiveIndex((i) => (i + 1) % rows.length);
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    suppressHoverRef.current = true;
                    if (rows.length) setActiveIndex((i) => (i - 1 + rows.length) % rows.length);
                    break;
                // Home/End belong to the caret while the search box has focus.
                case 'Home':
                    if (rows.length && event.target !== inputRef.current) {
                        event.preventDefault();
                        suppressHoverRef.current = true;
                        setActiveIndex(0);
                    }
                    break;
                case 'End':
                    if (rows.length && event.target !== inputRef.current) {
                        event.preventDefault();
                        suppressHoverRef.current = true;
                        setActiveIndex(rows.length - 1);
                    }
                    break;
                case 'Tab':
                    event.preventDefault();
                    cycleTab(event.shiftKey ? -1 : 1);
                    break;
                case 'Enter': {
                    event.preventDefault();
                    const row = rows[activeIndexRef.current];
                    if (row) {
                        goTo(row.url);
                    } else {
                        // Nothing highlighted: hand the whole query off to the list.
                        goTo(ticketsUrlRef.current());
                    }
                    break;
                }
                default:
                    break;
            }
        };

        document.addEventListener('keydown', onKeyDown, true);
        return () => document.removeEventListener('keydown', onKeyDown, true);
    }, [show, close, goTo, cycleTab]);

    if (!show) {
        return null;
    }

    const term = results.query || '';
    const hasQuery = query.replace(/^#/, '').trim().length >= MIN_TERM_LENGTH;
    // Filters alone are enough to show results.
    const hasCriteria = hasQuery || activeFilterCount > 0;
    // First page in flight with nothing on screen yet: show progress, not "0".
    const searching = loading && navRows.length === 0 && !failed;
    const showTab = (tab) => activeTab === 'all' || activeTab === tab;
    const indexOfRow = (kind, id) => navRows.findIndex((row) => row.kind === kind && row.id === id);
    const ticketsHidden = Math.max(0, (results.counts.tickets ?? 0) - (results.shown?.tickets ?? 0));

    /**
     * The tickets list, carrying whatever the palette is currently showing, so
     * "see all" widens the same result set instead of resetting it.
     *
     * The list hides closed tickets unless asked otherwise, so with no status
     * filter we name every status — the palette promised results regardless of
     * status and the handoff should keep that promise.
     */
    const ticketsUrl = () => {
        // Transferred-out tickets have their own view; it only understands text.
        if (filters.transferred === 'out') {
            return `/tickets/transferred${term ? `?text=${encodeURIComponent(term)}` : ''}`;
        }

        const listParams = new URLSearchParams();
        if (term) listParams.set('text', term);
        if (filters.departments.length) listParams.set('department', filters.departments.join(','));
        if (filters.members.length) listParams.set('assignee', filters.members.join(','));

        const statusIds = filters.statuses.length
            ? filters.statuses
            : scopes.statuses.map((status) => status.id);
        const statusNames = statusIds
            .map((id) => scopes.statuses.find((status) => status.id === id)?.name)
            .filter(Boolean);
        if (statusNames.length) {
            listParams.set('status', statusNames.join(','));
        }

        const search = listParams.toString();
        return `/tickets${search ? `?${search}` : ''}`;
    };
    ticketsUrlRef.current = ticketsUrl;

    /** Hover only moves the highlight when the pointer, not a key, caused it. */
    const hoverRow = (index) => {
        if (!suppressHoverRef.current) {
            setActiveIndex(index);
        }
    };

    /** Selected first, then options present in the results, then the rest. */
    const orderOptions = (options) => [...options].sort((a, b) => {
        if (a.active !== b.active) return a.active ? -1 : 1;
        if (!!a.hint !== !!b.hint) return a.hint ? -1 : 1;
        return String(a.label).localeCompare(String(b.label));
    });

    const facetGroups = (tabKey) => {
        if (tabKey === 'members') {
            return [{
                key: 'members',
                title: 'Filter by member',
                emptyLabel: 'No members in this workspace',
                onToggle: (id) => toggleFilterValue('members', id),
                options: orderOptions(scopes.members.map((member) => ({
                    value: member.id,
                    label: member.name,
                    active: filters.members.includes(member.id),
                    hint: inResults.members.has(member.id) ? 'in results' : null,
                }))),
            }];
        }

        if (tabKey === 'departments') {
            return [{
                key: 'departments',
                title: 'Filter by department',
                emptyLabel: 'No departments in this workspace',
                onToggle: (id) => toggleFilterValue('departments', id),
                options: orderOptions(scopes.departments.map((dept) => ({
                    value: dept.id,
                    label: dept.name,
                    color: dept.color,
                    active: filters.departments.includes(dept.id),
                    hint: inResults.departments.has(dept.id) ? 'in results' : null,
                }))),
            }];
        }

        // The Tickets tab carries the two ticket-shaped filters.
        return [
            {
                key: 'statuses',
                title: 'Filter by status',
                emptyLabel: 'No statuses available',
                onToggle: (id) => toggleFilterValue('statuses', id),
                options: orderOptions(scopes.statuses.map((status) => ({
                    value: status.id,
                    label: ucFirst(status.name),
                    // Same substitution as the badge, so the dot for "closed" is
                    // not an invisible speck in this menu either.
                    color: statusColor(status),
                    active: filters.statuses.includes(status.id),
                    hint: inResults.statuses.has(status.id) ? 'in results' : null,
                }))),
            },
            {
                key: 'transferred',
                title: 'Transferred tickets',
                // Mutually exclusive, so picking one replaces the other.
                onToggle: (value) => setFilters((prev) => ({
                    ...prev,
                    transferred: prev.transferred === value ? '' : value,
                })),
                options: TRANSFER_OPTIONS.map((option) => ({
                    value: option.value,
                    label: option.label,
                    active: filters.transferred === option.value,
                })),
            },
        ];
    };

    /** How many filters a given tab's menu currently has applied. */
    const facetFilterCount = (tabKey) => {
        if (tabKey === 'members') return filters.members.length;
        if (tabKey === 'departments') return filters.departments.length;
        if (tabKey === 'tickets') return filters.statuses.length + (filters.transferred ? 1 : 0);
        return 0;
    };

    const renderTicketRow = (ticket) => {
        const rowIndex = indexOfRow('ticket', ticket.id);
        return (
            <div
                key={`ticket-${ticket.id}`}
                className={`ck-gs-trow ${rowIndex === activeIndex ? 'ck-gs-active' : ''}`}
                onMouseEnter={() => hoverRow(rowIndex)}
                onClick={() => goTo(ticket.url)}
                role="option"
                aria-selected={rowIndex === activeIndex}
            >
                <span className="ck-gs-ticon"><BsTicketPerforated /></span>

                <span className="ck-gs-tmain">
                    <span className="ck-gs-tline">
                        {ticket.ticket_id && (
                            <span className="ck-gs-tid"><Highlight text={`#${ticket.ticket_id}`} term={term} /></span>
                        )}
                        <b className="ck-gs-ttitle"><Highlight text={ticket.title} term={term} /></b>

                        {ticket.status?.name && (
                            <span
                                className={`ck-gs-badge st-${statusSlug(ticket.status.name)} ${filters.statuses.includes(ticket.status.id) ? 'is-on' : ''}`}
                                style={statusBadgeStyle(ticket.status)}
                                title="Filter by this status"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    toggleFilterValue('statuses', ticket.status.id);
                                }}
                            >
                                {ticket.status.name}
                            </span>
                        )}

                        {ticket.is_transferred_out && (
                            <span
                                className={`ck-gs-badge ck-gs-badge-transfer ${filters.transferred === 'out' ? 'is-on' : ''}`}
                                title={`Transferred to ${ticket.transferred_to}. Click to show only transferred-out tickets`}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setFilters((prev) => ({ ...prev, transferred: prev.transferred === 'out' ? '' : 'out' }));
                                }}
                            >
                                <BsArrowRightShort />{ticket.transferred_to || 'Transferred'}
                            </span>
                        )}
                        {ticket.is_transferred_in && !ticket.is_transferred_out && (
                            <span
                                className={`ck-gs-badge ck-gs-badge-transfer-in ${filters.transferred === 'in' ? 'is-on' : ''}`}
                                title={`Received from ${ticket.transferred_from}. Click to show only received tickets`}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setFilters((prev) => ({ ...prev, transferred: prev.transferred === 'in' ? '' : 'in' }));
                                }}
                            >
                                {ticket.transferred_from || 'Received'}
                            </span>
                        )}
                        {ticket.is_deleted && (
                            <span className="ck-gs-badge ck-gs-badge-deleted" title="In trash"><BsTrash /> Deleted</span>
                        )}
                    </span>

                    <span className="ck-gs-tmeta">
                        {ticket.department?.name && (
                            <a
                                className={`ck-gs-deptlink ${filters.departments.includes(ticket.department.id) ? 'is-on' : ''}`}
                                title="Filter by this department"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    toggleFilterValue('departments', ticket.department.id);
                                }}
                            >
                                {ticket.department.name}
                            </a>
                        )}
                        {ticket.assignees?.length > 0 && (
                            <span className="ck-gs-tmeta-part">
                                {ticket.assignees.map((assignee) => (
                                    <a
                                        key={assignee.id}
                                        className={`ck-gs-memberlink ${filters.members.includes(assignee.id) ? 'is-on' : ''}`}
                                        title="Filter by this member"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            toggleFilterValue('members', assignee.id);
                                        }}
                                    >
                                        {assignee.name}
                                    </a>
                                ))}
                            </span>
                        )}
                        {/* Usually the only person on a transferred ticket, and the
                            reason a member filter matched it. */}
                        {ticket.transferred_by && (
                            <span className="ck-gs-tmeta-part">transferred by {ticket.transferred_by}</span>
                        )}
                        {!ticket.assignees?.length && !ticket.transferred_by && ticket.submitter && (
                            <span className="ck-gs-tmeta-part">{ticket.submitter}</span>
                        )}
                        {ticket.updated_ago && <span className="ck-gs-tmeta-part">updated {ticket.updated_ago}</span>}
                    </span>

                    {ticket.excerpt && (
                        <span className="ck-gs-texcerpt"><Highlight text={ticket.excerpt} term={term} /></span>
                    )}
                </span>
            </div>
        );
    };

    const renderMemberRow = (member) => {
        const rowIndex = indexOfRow('member', member.id);
        return (
            <div
                key={`member-${member.id}`}
                className={`ck-gs-mrow ${rowIndex === activeIndex ? 'ck-gs-active' : ''}`}
                onMouseEnter={() => hoverRow(rowIndex)}
                onClick={() => goTo(member.url)}
                role="option"
                aria-selected={rowIndex === activeIndex}
            >
                <span className="ck-gs-avwrap">
                    {member.profile_image
                        ? <img src={member.profile_image} alt="" />
                        : <span className="ck-gs-avfallback">{String(member.name ?? '?').charAt(0).toUpperCase()}</span>}
                    <span className={`ck-gs-presence ${member.active ? 'on' : ''}`} />
                </span>
                <b><Highlight text={member.name} term={term} /></b>
                <span className="ck-gs-handle"><Highlight text={member.email} term={term} /></span>
                {member.title && <span className="ck-gs-mtitle">{member.title}</span>}
                <span
                    className={`ck-gs-scopebtn ${filters.members.includes(member.id) ? 'is-on' : ''}`}
                    title="Add this member to the filters"
                    onClick={(event) => {
                        event.stopPropagation();
                        toggleFilterValue('members', member.id);
                    }}
                >
                    {filters.members.includes(member.id) ? 'Filtering' : 'Filter'}
                </span>
                <span className="ck-gs-select">View tickets</span>
            </div>
        );
    };

    const renderDepartmentCard = (dept) => {
        const rowIndex = indexOfRow('department', dept.id);
        return (
            <a
                key={`dept-${dept.id}`}
                className={`ck-gs-card ${rowIndex === activeIndex ? 'ck-gs-active' : ''}`}
                onMouseEnter={() => hoverRow(rowIndex)}
                onClick={() => goTo(dept.url)}
                role="option"
                aria-selected={rowIndex === activeIndex}
            >
                <span className="ck-gs-cardbullet" style={{ backgroundColor: dept.color || '#18AEE3' }} />
                <span className="ck-gs-cardmain">
                    <span className="ck-gs-cardname"><Highlight text={dept.name} term={term} /></span>
                    <span className="ck-gs-cardmeta">Department</span>
                </span>
                <span
                    className={`ck-gs-scopebtn ${filters.departments.includes(dept.id) ? 'is-on' : ''}`}
                    title="Add this department to the filters"
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        toggleFilterValue('departments', dept.id);
                    }}
                >
                    {filters.departments.includes(dept.id) ? 'Filtering' : 'Filter'}
                </span>
            </a>
        );
    };

    return createPortal(
        <div
            className="ck-gs-modal"
            onMouseMove={() => { suppressHoverRef.current = false; }}
            // Only a click on the backdrop itself closes. The card and the results
            // sit inside ck-gs-panel, which also owns the gap between them, so
            // clicking that gap can never reach this handler.
            onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}
        >
            <div className="ck-gs-panel">
                <div className="ck-gs-searchcard">
                    <div className="ck-gs-inputrow">
                        <IoSearchOutline className="ck-gs-inputicon" />
                        <input
                            ref={inputRef}
                            type="text"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder="Search tickets, members, departments…"
                            autoComplete="off"
                            spellCheck="false"
                            aria-label="Search"
                        />
                        {query.length > 0 && (
                            <a className="ck-gs-clear" onClick={reset} title="Clear"><IoCloseCircle /></a>
                        )}
                    </div>

                    <div className="ck-gs-hintrow">
                        <span>Navigate <kbd>↑</kbd> <kbd>↓</kbd></span>
                        <span>Switch tabs <kbd>tab</kbd></span>
                        {activeFilterCount > 0 && (
                            <a className="ck-gs-clearfilters" onClick={() => { setFilters(EMPTY_FILTERS); focusInput(); }}>
                                Clear {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}
                            </a>
                        )}
                        <span className="ck-gs-closehint">Close <kbd>esc</kbd></span>
                    </div>
                </div>

                {/* The panel is always mounted so the tab filters are available from
                    the moment the palette opens; only the result body waits for a
                    query or a filter. */}
                <div className="ck-gs-results">
                        {/* Every tab is always present, so the filter menus they carry
                            are reachable before anything is typed. */}
                        <div className="ck-gs-tabs">
                            {TABS.map((tab) => {
                                const count = searching ? null : (results.counts[tab.key] ?? 0);
                                const hasFacet = FACET_TABS.includes(tab.key);
                                const applied = facetFilterCount(tab.key);

                                return (
                                    <span className="ck-gs-tabwrap" key={tab.key}>
                                        <button
                                            type="button"
                                            className={`ck-gs-tab ${activeTab === tab.key ? 'active' : ''} ${hasFacet ? 'has-facet' : ''}`}
                                            title={hasFacet ? 'Click again to open its filters' : undefined}
                                            onClick={(event) => {
                                                // Second click on the tab you are already
                                                // on opens its filters, as in Claritask.
                                                if (hasFacet && activeTab === tab.key) {
                                                    toggleFacet(tab.key, event.currentTarget);
                                                    return;
                                                }
                                                setActiveTab(tab.key);
                                                closeFacet();
                                                focusInput();
                                            }}
                                        >
                                            {tab.icon}{tab.label}
                                            <span className="ck-gs-tabcount">{count === null ? '…' : count}</span>
                                        </button>

                                        {hasFacet && (
                                            <button
                                                type="button"
                                                className={`ck-gs-tabcaret ${facet?.tab === tab.key ? 'open' : ''} ${applied > 0 ? 'is-on' : ''}`}
                                                title={`Filters for ${tab.label.toLowerCase()}`}
                                                aria-label={`Filters for ${tab.label}`}
                                                aria-expanded={facet?.tab === tab.key}
                                                onClick={(event) => toggleFacet(tab.key, event.currentTarget.parentElement)}
                                            >
                                                {applied > 0 && <span className="ck-gs-tabapplied">{applied}</span>}
                                                <IoChevronDown />
                                            </button>
                                        )}
                                    </span>
                                );
                            })}

                            {loading && <span className="ck-gs-loading"><span className="ck-gs-spinner" />Searching…</span>}
                        </div>

                        {facet && (
                            <FacetMenu
                                anchor={facet}
                                groups={facetGroups(facet.tab)}
                                onClose={closeFacet}
                            />
                        )}

                        {/* Refining a query keeps the previous rows rather than
                            blanking the list, but dims them so it is clear they are
                            about to be replaced. */}
                        <div
                            className={`ck-gs-body ${loading && navRows.length > 0 ? 'is-stale' : ''}`}
                            ref={listRef}
                            role="listbox"
                            onScroll={handleBodyScroll}
                        >
                            {!hasCriteria && (
                                <div className="ck-gs-empty ck-gs-hintempty">
                                    Type to search tickets, members and departments, or filter
                                    from the tabs above.
                                </div>
                            )}

                            {hasCriteria && failed && <div className="ck-gs-empty">Search is unavailable right now. Please try again.</div>}

                            {searching && (
                                <div className="ck-gs-empty ck-gs-searching">
                                    <span className="ck-gs-spinner" />Searching…
                                </div>
                            )}

                            {/* An empty tab explains itself and offers a way onward,
                                rather than bouncing the user back to All. */}
                            {hasCriteria && !failed && navRows.length === 0 && !loading && (
                                <div className="ck-gs-empty">
                                    {hasQuery
                                        ? <>No {TAB_NOUNS[activeTab]} match <b>“{query.trim()}”</b></>
                                        : <>No {TAB_NOUNS[activeTab]} match the current filters</>}
                                    {hasQuery && activeFilterCount > 0 && ' with the current filters'}
                                    {'.'}

                                    <div className="ck-gs-emptyactions">
                                        {activeFilterCount > 0 && (
                                            <a className="ck-gs-clearfilters" onClick={() => { setFilters(EMPTY_FILTERS); focusInput(); }}>
                                                Clear filters
                                            </a>
                                        )}
                                        {/* Other tabs may well have matches for the same search. */}
                                        {activeTab !== 'all' && (results.counts.all ?? 0) > 0 && (
                                            <a
                                                className="ck-gs-clearfilters"
                                                onClick={() => { setActiveTab('all'); focusInput(); }}
                                            >
                                                Show all {results.counts.all} result{results.counts.all === 1 ? '' : 's'}
                                            </a>
                                        )}
                                    </div>
                                </div>
                            )}

                            {showTab('members') && results.members.length > 0 && (
                                <>
                                    <div className="ck-gs-head">
                                        Members <span className="ck-gs-count">{results.counts.members}</span>
                                    </div>
                                    {results.members.map(renderMemberRow)}
                                </>
                            )}

                            {showTab('tickets') && results.tickets.length > 0 && (
                                <>
                                    <div className="ck-gs-head">
                                        Tickets <span className="ck-gs-count">{results.counts.tickets}</span>
                                        {ticketsHidden > 0 && (
                                            <span className="ck-gs-showing">
                                                showing {results.tickets.length}
                                            </span>
                                        )}
                                        <a className="ck-gs-seeall" onClick={() => goTo(ticketsUrl())}>
                                            open in tickets list
                                        </a>
                                    </div>
                                    <div className="ck-gs-tasklist">{results.tickets.map(renderTicketRow)}</div>

                                    {/* Scrolling to the bottom pulls the next page in. */}
                                    {results.has_more && (
                                        <div className="ck-gs-more">
                                            {loadingMore
                                                ? <><span className="ck-gs-spinner" />Loading more…</>
                                                : <a onClick={loadMore}>Load {Math.max(0, ticketsHidden)} more</a>}
                                        </div>
                                    )}
                                </>
                            )}

                            {showTab('departments') && results.departments.length > 0 && (
                                <>
                                    <div className="ck-gs-head">
                                        Departments <span className="ck-gs-count">{results.counts.departments}</span>
                                    </div>
                                    <div className="ck-gs-cards">{results.departments.map(renderDepartmentCard)}</div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>,
        document.body
    );
};

export default GlobalSearch;
